import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { DownloadIcon, MaximizeArrowsIcon } from '@/UI/Icons';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenHor } from '@/UI/styles';
import { isTextAsset } from '@/components/Common/ImageList/utils';
import { color } from '@/colors';
import { useUserWorkflowRole } from '@/state/workflow.state';
import type { UploadedAsset, TextAsset } from '@/types/api/assets';
import type { FileKind } from '@/designer/designer';

const TOOLTIP_BOTTOM_STYLE = {
  popper: {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, -8],
        },
      },
    ],
  },
};

export interface ActionButtonsToolbarProps {
  isSingleAssetMode: boolean;
  isBatchViewMode: boolean;
  asset?: UploadedAsset | FileKind;
  showExpandToGalleryButton: boolean;
  showDownloadAllButton: boolean;
  onOpenGallery?: () => void;
  onDownloadAll?: () => void;
}

export const ActionButtonsToolbar = ({
  isSingleAssetMode,
  isBatchViewMode,
  asset,
  showExpandToGalleryButton,
  showDownloadAllButton,
  onOpenGallery,
  onDownloadAll,
}: ActionButtonsToolbarProps) => {
  const { t } = useTranslation();
  const role = useUserWorkflowRole();
  const isText = isTextAsset(asset);

  const onDownloadAllClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDownloadAll?.();
    },
    [onDownloadAll],
  );

  const onOpenGalleryClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onOpenGallery?.();
    },
    [onOpenGallery],
  );

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        pointerEvents: 'none',
      }}
    >
      <FlexCenHor sx={{ pointerEvents: 'all' }}>
        {isSingleAssetMode && isText && (asset as TextAsset)?.originalValue && (
          <Typography
            variant="label-sm-rg"
            color={color.White80_T}
            sx={{
              backgroundColor: color.White16_T,
              px: 0.5,
              borderRadius: 0.5,
              position: 'relative',
              alignSelf: 'center',
            }}
          >
            {t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.LLM_EDITED)}
          </Typography>
        )}
        {showExpandToGalleryButton && (
          <Tooltip
            title={t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.OPEN_IN_GALLERY)}
            slotProps={{ ...TOOLTIP_BOTTOM_STYLE }}
            disableInteractive
          >
            <AppIconButton onClick={onOpenGalleryClick} width={32} height={32} mode="on-light">
              <MaximizeArrowsIcon />
            </AppIconButton>
          </Tooltip>
        )}
        {role === 'editor' && onDownloadAll && showDownloadAllButton && (
          <Tooltip
            title={
              isSingleAssetMode
                ? t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.DOWNLOAD_CURRENT)
                : isBatchViewMode
                  ? t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.DOWNLOAD_BATCH)
                  : t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.DOWNLOAD_ALL)
            }
            slotProps={{ ...TOOLTIP_BOTTOM_STYLE }}
            disableInteractive
          >
            <AppIconButton onClick={onDownloadAllClick} width={32} height={32} mode="on-light">
              <DownloadIcon />
            </AppIconButton>
          </Tooltip>
        )}
      </FlexCenHor>
    </Box>
  );
};
